import { Head, router } from '@inertiajs/react';
import { useConnectionStatus, useEcho } from '@laravel/echo-react';
import {
    NoticeType
} from '@onwardtravelsolutions/package-react/Enums';
import {
    usePaginatedRequest,
} from '@onwardtravelsolutions/package-react/Hooks';
import { Paginator } from '@onwardtravelsolutions/package-react/Types';
import { useEffect, useRef, useState } from 'react';
import { Rule, Section } from '../Components';
import { Banner, BannerHeader, BannerText } from '../Components/Banners';
import { NoticeList } from '../Components/Notices';
import PaginationToolbar from '../Components/Pagination/PaginationToolbar';

type SubmissionsProps = {};

/**
 * @todo convert `FormSubmittedEvent`/`reset()` to `addSubmission()` when state listeners support
 */
const Submissions = ({ }: SubmissionsProps) => {
    const status = useConnectionStatus();
    const { response, paginator, isComplete } = usePaginatedRequest(
        'api/submission',
        1,
        25,
        {
            order: 'id:desc',
        },
    );
    const lastPaginator = useRef<Paginator | null>(null);
    const [submissions, setSubmissions] = useState<any[]>([]);
    useEffect(() => {
        if (isComplete && response) {
            if (response.isSuccessful) {
                setSubmissions(
                    Array.isArray(response.data) ? response.data : [],
                );
            }
            if (paginator.isPaginatable) {
                lastPaginator.current = paginator;
            } else {
                lastPaginator.current = null;
            }
        }
    }, [isComplete, response]);

    const submissionNotices = response?.notices?.filter(
        (notice) =>
            notice.type === NoticeType.Error ||
            notice.type === NoticeType.Warning,
    );

    useEcho(
        'submissions',
        ['.form.submitted'],
        (event: any) => {
            console.log('FormSubmittedEvent Received', event);
            paginator?.refresh();
        },
    );

    return (
        <>
            <Head>
                <title>Submissions</title>
                <meta
                    name='description'
                    content='View recent form submissions on this application.'
                />
            </Head>

            <Banner className='py-38'>
                <BannerHeader>Form Submissions</BannerHeader>
                <Rule className='mx-auto w-1/2 max-w-60' />
                <BannerText>
                    View recent form submissions on this application.
                </BannerText>
            </Banner>

            {submissionNotices && (
                <NoticeList id={'submissions'} notices={submissionNotices} />
            )}

            <Section className='py-10 font-base' id='submissions'>
                {lastPaginator.current && (
                    <PaginationToolbar
                        paginator={lastPaginator.current}
                        className='mb-10'
                    />
                )}
                <div className='bg-white dark:bg-gray-950/50 rounded-lg outline outline-white/5 overflow-auto not-prose'>
                    <table className='w-full text-sm border-collapse table-auto'>
                        <thead>
                            <tr>
                                <th className='p-4 pb-3 pl-8 border-gray-200 dark:border-gray-600 border-b font-medium text-gray-400 dark:text-gray-200 text-left'>
                                    ID
                                </th>
                                <th className='p-4 pb-3 pl-8 border-gray-200 dark:border-gray-600 border-b font-medium text-gray-400 dark:text-gray-200 text-left'>
                                    Form
                                </th>
                                <th className='p-4 pb-3 pl-8 border-gray-200 dark:border-gray-600 border-b font-medium text-gray-400 dark:text-gray-200 text-left'>
                                    GUID
                                </th>
                                <th className='p-4 pr-0 pb-3 pl-8 border-gray-200 dark:border-gray-600 border-b font-medium text-gray-400 dark:text-gray-200 text-left'>
                                    Submitted
                                </th>
                            </tr>
                        </thead>
                        <tbody className='bg-white dark:bg-zinc-800 dark:text-gray-400'>
                            {submissions.length ? (
                                submissions.map(
                                    ({ id, form, guid, created_at }) => {
                                        const date = new Date(
                                            created_at * 1000,
                                        );
                                        return (
                                            <tr
                                                key={`submission.${guid}`}
                                                className='dark:even:bg-neutral-900 even:bg-gray-50 text-gray-500 hover:text-primary dark:text-gray-300 cursor-pointer'
                                                title={'View Submission'}
                                                onClick={() =>
                                                    router.visit(
                                                        `submissions/${id}`,
                                                        {
                                                            data: {
                                                                previous:
                                                                    window
                                                                        .location
                                                                        .href,
                                                            },
                                                        },
                                                    )
                                                }
                                            >
                                                <td className='p-4 pl-8 border-gray-100 dark:border-gray-700 border-b cursor-pointer'>
                                                    {id}
                                                </td>
                                                <td className='p-4 pl-8 border-gray-100 dark:border-gray-700 border-b capitalize'>
                                                    {form}
                                                </td>
                                                <td className='p-4 pl-8 border-gray-100 dark:border-gray-700 border-b'>
                                                    {guid}
                                                </td>
                                                <td className='p-4 pr-0 pl-8 border-gray-100 dark:border-gray-700 border-b'>
                                                    {date.toLocaleTimeString()}{' '}
                                                    {date.toLocaleDateString()}
                                                </td>
                                            </tr>
                                        );
                                    },
                                )
                            ) : (
                                <tr>
                                    <td
                                        className={
                                            'text-center p-8 text-gray-400'
                                        }
                                        colSpan={4}
                                    >
                                        There are no Form Submissions to
                                        display.
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td
                                    className={
                                        'text-center p-8 text-gray-400 text-xs'
                                    }
                                    colSpan={4}
                                >
                                    Submission Events Status: {status}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {lastPaginator.current && (
                    <PaginationToolbar
                        paginator={lastPaginator.current}
                        className='mt-10'
                    />
                )}
            </Section>
        </>
    );
};

export default Submissions;
